import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, ShieldCheck, ShieldX } from "lucide-react";

export default function AdminResetPassword() {
  useSeo({ title: "Set New Password | Maha Sampada", noindex: true });
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [account, setAccount] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/reset-password/verify?token=${encodeURIComponent(token)}`)
      .then((r) => { if (r.data?.valid) { setState("valid"); setAccount(r.data); } else { setState("invalid"); } })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setState("done");
      toast.success("Password updated. You can now sign in.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-charcoal px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-3xl bg-white p-10">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-maroon text-cream font-display text-xl font-bold">M</div>
          <div><div className="font-display text-2xl font-bold text-charcoal">Maha Sampada</div><div className="overline text-[9px] text-saffron">Admin Password Reset</div></div>
        </div>

        {state === "checking" && (
          <div className="mt-10 flex items-center gap-2 font-accent text-sm text-charcoal" data-testid="reset-checking">
            <Loader2 className="h-4 w-4 animate-spin" /> Verifying your link…
          </div>
        )}

        {state === "invalid" && (
          <div className="mt-8" data-testid="reset-invalid">
            <div className="flex items-center gap-2 text-maroon"><ShieldX className="h-5 w-5" /><h1 className="font-display text-xl font-semibold">Link invalid or expired</h1></div>
            <p className="mt-3 font-accent text-sm text-muted-foreground">This password link is no longer valid. Ask a Super Admin to send a fresh reset link.</p>
            <Button data-testid="reset-goto-login" onClick={() => navigate("/admin/login")} className="mt-6 rounded-full bg-saffron text-white hover:bg-saffron-dark">Back to sign in</Button>
          </div>
        )}

        {state === "valid" && (
          <>
            <div className="mt-8 flex items-center gap-2 text-forest"><ShieldCheck className="h-5 w-5" /><h1 className="font-display text-xl font-semibold text-charcoal">Choose a new password</h1></div>
            {account?.email && <p className="mt-2 font-accent text-sm text-muted-foreground">for <strong>{account.email}</strong></p>}
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold">New Password (min 8 chars)</Label><PasswordInput testId="reset-new-password" value={pw} onChange={(e) => setPw(e.target.value)} required /></div>
              <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold">Confirm Password</Label><PasswordInput testId="reset-confirm-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></div>
              <Button type="submit" data-testid="reset-submit" disabled={saving} className="w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : <><Lock className="mr-2 h-4 w-4" />Set Password</>}
              </Button>
            </form>
          </>
        )}

        {state === "done" && (
          <div className="mt-8" data-testid="reset-done">
            <div className="flex items-center gap-2 text-forest"><ShieldCheck className="h-5 w-5" /><h1 className="font-display text-xl font-semibold text-charcoal">Password updated</h1></div>
            <p className="mt-3 font-accent text-sm text-muted-foreground">Your password has been set and all previous sessions for this account were signed out.</p>
            <Button data-testid="reset-signin" onClick={() => navigate("/admin/login")} className="mt-6 rounded-full bg-saffron text-white hover:bg-saffron-dark">Sign in now</Button>
          </div>
        )}
      </div>
    </div>
  );
}
